import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '#environments';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private appInsights: ApplicationInsights | null = null;
  private initialized = false;

  /**
   * Initialize Application Insights with the instrumentation key from environment
   */
  initialize(): void {
    if (this.initialized || !environment.appInsightsInstrumentationKey) {
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: environment.appInsightsInstrumentationKey,
          enableAutoRouteTracking: true,
          autoTrackPageVisitTime: true,
          disableExceptionTracking: false,
        },
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Manually track initial page view
      this.initialized = true;
    } catch (error) {
      console.warn('Failed to initialize Application Insights:', error);
    }
  }

  /**
   * Track a custom event
   * @param name Event name
   * @param properties Optional custom properties
   * @param measurements Optional custom measurements
   */
  trackEvent(name: string, properties?: Record<string, string>, measurements?: Record<string, number>): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.trackEvent({
      name,
      properties,
      measurements,
    });
  }

  /**
   * Track a page view
   * @param name Page name (optional, uses route by default)
   * @param url Page URL (optional)
   * @param properties Optional custom properties
   */
  trackPageView(name?: string, url?: string, properties?: Record<string, string>): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.trackPageView({
      name,
      uri: url,
      properties,
    });
  }

  /**
   * Track an exception/error
   * @param error Error object or string
   * @param severityLevel Severity level (0=verbose, 1=information, 2=warning, 3=error, 4=critical)
   * @param properties Optional custom properties
   */
  trackException(error: Error | string, severityLevel = 3, properties?: Record<string, string>): void {
    if (!this.appInsights) {
      return;
    }

    const exception = typeof error === 'string' ? new Error(error) : error;

    this.appInsights.trackException({
      exception,
      severityLevel,
      properties,
    });
  }

  /**
   * Track a trace/log message
   * @param message Message to log
   * @param severityLevel Severity level (0=verbose, 1=information, 2=warning, 3=error, 4=critical)
   * @param properties Optional custom properties
   */
  trackTrace(message: string, severityLevel = 1, properties?: Record<string, string>): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.trackTrace({
      message,
      severityLevel,
      properties,
    });
  }

  /**
   * Track a metric (numeric measurement)
   * @param name Metric name
   * @param average Average value
   * @param sampleCount Sample count (optional)
   * @param min Minimum value (optional)
   * @param max Maximum value (optional)
   * @param properties Optional custom properties
   */
  trackMetric(
    name: string,
    average: number,
    sampleCount?: number,
    min?: number,
    max?: number,
    properties?: Record<string, string>
  ): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.trackEvent({
      name: `Metric_${name}`,
      properties,
      measurements: {
        average,
        sampleCount: sampleCount || 1,
        ...(min !== undefined && { min }),
        ...(max !== undefined && { max }),
      },
    });
  }

  /**
   * Set user context
   * @param userId User ID
   * @param accountId Account/Tenant ID (optional)
   */
  setUserContext(userId: string, accountId?: string): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.setAuthenticatedUserContext(userId, accountId);
  }

  /**
   * Clear user context
   */
  clearUserContext(): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.clearAuthenticatedUserContext();
  }

  /**
   * Set custom property for all events
   * @param key Property key
   * @param value Property value
   */
  setCustomProperty(key: string, value: string | number | boolean): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.config.extensionConfig = [
      {
        identifier: 'AppInsightsPropertiesPlugin',
        config: {
          customProperties: {
            [key]: value,
          },
        },
      },
    ];
  }

  /**
   * Flush pending events
   */
  flush(): void {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.flush();
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}
